/**
 * Lecture d'un export JSON Testo (« tjf », Testo JSON format).
 *
 * Source : schéma publié par Testo, https://github.com/testo/tjf (Apache-2.0),
 * `schema/slim-schema-v1.4.0.json` et `schema/measurementCollection-v1.1.0.json`,
 * plus les exemples froid du dépôt (`TJF Examples/Schema1.0.2/refrigeration_dmr`
 * et `smartprobes/04_ac_refrigeration.json`). L'application testo Smart et les
 * manifolds 550/557/550s/557s exportent ce format.
 *
 * Trois réalités du format, constatées dans ces exemples, dictent le code :
 * - le NOM d'un canal vit tantôt dans `type.name` (« LP », « toh »), tantôt
 *   dans `values[].additionalData[valueType].description` (« ND », « HD ») ;
 *   il est de plus traduit dans la langue de l'application. On reconnaît donc
 *   les libellés connus, et tout canal non reconnu est CONSERVÉ pour être
 *   affecté à la main — jamais deviné ni jeté en silence ;
 * - une sonde absente n'est pas omise : elle vaut 4294967295 ou un flottant
 *   dénormalisé, avec « ---- » pour texte. Ces valeurs sont écartées ;
 * - l'horodatage des valeurs peut être celui d'une horloge non réglée
 *   (1970) : seule la date du relevé entier fait foi.
 *
 * Ce module n'écrit rien : il transforme un texte en relevé à RELIRE avant de
 * l'inscrire sur une fiche. Aucun fichier Testo n'est envoyé au serveur.
 */

import { CERFA_CONTENT_LABELS } from './cerfa.js';

/** Grandeurs d'un relevé frigorifique reportées sur la fiche d'intervention. */
export const TESTO_QUANTITIES = [
  'lowPressure',
  'highPressure',
  'evaporationTemp',
  'condensationTemp',
  'suctionLineTemp',
  'liquidLineTemp',
  'superheat',
  'subcooling',
] as const;
export type TestoQuantity = (typeof TESTO_QUANTITIES)[number];

export interface TestoReading {
  /** Grandeur reconnue, ou `null` : canal conservé, à affecter à la main. */
  quantity: TestoQuantity | null;
  /** Libellé tel que l'appareil l'a écrit (« LP », « ND », « toh »…). */
  label: string;
  value: number;
  unit: string;
  /** Valeur lisible, virgule décimale et unité : « 1,01 bar ». */
  display: string;
  /** Nombre de valeurs valides du canal ; la dernière est retenue. */
  samples: number;
}

export interface TestoMeasurement {
  schemaVersion: string | null;
  /** Nom du type de mesure, dans la langue de l'appareil (« Kälte », « Froid »). */
  measurementType: string | null;
  /** Date du relevé au format ISO, sans conversion de fuseau ; `null` si absente. */
  measuredAt: string | null;
  devices: { name: string; serial: string }[];
  refrigerant: string | null;
  readings: TestoReading[];
  /** Canaux présents mais sans valeur exploitable (sonde non branchée). */
  emptyChannels: number;
}

export type TestoParseErrorCode = 'too_large' | 'not_json' | 'not_testo' | 'no_measurement';

const MESSAGES: Record<TestoParseErrorCode, string> = {
  too_large: 'Ce fichier dépasse 2 Mo : ce n’est pas un export de mesure Testo.',
  not_json: 'Ce fichier n’est pas un JSON lisible. Exportez la mesure au format JSON depuis l’application testo Smart.',
  not_testo: 'Ce JSON n’a pas la structure d’un export Testo (canaux de mesure absents).',
  no_measurement: 'Cet export Testo ne contient aucune valeur exploitable.',
};

export class TestoParseError extends Error {
  readonly code: TestoParseErrorCode;
  constructor(code: TestoParseErrorCode) {
    super(MESSAGES[code]);
    this.name = 'TestoParseError';
    this.code = code;
  }
}

/** Budgets : un export de mesure pèse quelques kilo-octets. */
export const TESTO_MAX_CHARS = 2_000_000;
const MAX_MEASUREMENTS = 50;
const MAX_CHANNELS = 200;
const MAX_LABEL = 60;

/** Sentinelle « pas de valeur » des manifolds (0xFFFFFFFF). */
const NO_VALUE = 4294967295;

/**
 * Libellés connus, après normalisation (minuscules, sans espace ni point).
 * Allemand et anglais viennent des exemples Testo ; « bp » et « sr » sont les
 * abréviations françaises usuelles. Un libellé absent d'ici n'est pas une
 * erreur : le canal reste proposé à l'affectation manuelle.
 */
const KNOWN_LABELS: Record<string, TestoQuantity> = {
  lp: 'lowPressure', nd: 'lowPressure', bp: 'lowPressure', plow: 'lowPressure',
  hp: 'highPressure', hd: 'highPressure', phigh: 'highPressure',
  to: 'evaporationTemp', t0: 'evaporationTemp',
  tc: 'condensationTemp',
  toh: 'suctionLineTemp',
  tcu: 'liquidLineTemp',
  'δtoh': 'superheat', sh: 'superheat',
  'δtcu': 'subcooling', sc: 'subcooling', sr: 'subcooling',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(value: unknown, max = MAX_LABEL): string {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value !== 'string') return '';
  // Certains champs portent du balisage d'affichage (« P<sub>rel</sub> »).
  return value.replace(/<[^>]*>/g, '').trim().slice(0, max);
}

function normalizeLabel(label: string): string {
  return label.toLowerCase().replace(/[\s._-]/g, '');
}

function valueTypeOf(value: Record<string, unknown>): string {
  if (!Array.isArray(value.additionalData)) return '';
  for (const item of value.additionalData) {
    if (isRecord(item) && item.name === 'valueType') return text(item.description);
  }
  return '';
}

function isUsable(value: Record<string, unknown>): value is Record<string, unknown> & { value: number } {
  const raw = value.value;
  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw === NO_VALUE) return false;
  // « ---- » : l'appareil affiche des tirets quand la sonde manque, même si le
  // champ numérique porte un résidu (flottant dénormalisé dans les exemples).
  if (typeof value.description === 'string' && /^\s*-{2,}\s*$/.test(value.description)) return false;
  if (typeof value.status === 'string' && /invalid|overrange|underrange|empty/i.test(value.status)) {
    return false;
  }
  return true;
}

function formatNumber(value: Record<string, unknown> & { value: number }): { value: number; shown: string } {
  // Le texte de l'appareil porte déjà la bonne précision : on le préfère à un
  // flottant simple précision (1.0099999904632568 pour « 1,01 »).
  const described = typeof value.description === 'string' ? value.description.trim() : '';
  if (/^[+-]?\d+([.,]\d+)?$/.test(described)) {
    return { value: Number(described.replace(',', '.')), shown: described.replace('.', ',') };
  }
  const rounded = Math.round(value.value * 1000) / 1000;
  return { value: rounded, shown: String(rounded).replace('.', ',') };
}

function parseChannel(channel: unknown): TestoReading | 'empty' | null {
  if (!isRecord(channel) || !Array.isArray(channel.values)) return null;
  const usable = channel.values.filter(isRecord).filter(isUsable);
  const last = usable.at(-1);
  if (!last) return 'empty';
  const type = isRecord(channel.type) ? channel.type : {};
  const candidates = [valueTypeOf(last), text(type.name), text(type.description)].filter(Boolean);
  let quantity: TestoQuantity | null = null;
  for (const candidate of candidates) {
    quantity = KNOWN_LABELS[normalizeLabel(candidate)] ?? null;
    if (quantity) break;
  }
  const unit = isRecord(channel.unit) ? text(channel.unit.name, 12) : '';
  const number = formatNumber(last);
  return {
    quantity,
    label: candidates[0] ?? '',
    value: number.value,
    unit,
    display: unit ? `${number.shown} ${unit}` : number.shown,
    samples: usable.length,
  };
}

function parseTimestamp(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    // Secondes ou millisecondes selon l'application (les deux sont admis par le schéma).
    const date = new Date(value < 1e11 ? value * 1000 : value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 32);
  return null;
}

function parseDevices(device: unknown): { name: string; serial: string }[] {
  const list = Array.isArray(device) ? device : [device];
  const seen = new Set<string>();
  const devices: { name: string; serial: string }[] = [];
  for (const item of list.slice(0, 20)) {
    if (!isRecord(item)) continue;
    const entry = { name: text(item.name), serial: text(item.serial) };
    const key = `${entry.name}|${entry.serial}`;
    if ((!entry.name && !entry.serial) || seen.has(key)) continue;
    seen.add(key);
    devices.push(entry);
  }
  return devices;
}

function parseRefrigerant(properties: unknown): string | null {
  if (!Array.isArray(properties)) return null;
  for (const property of properties) {
    if (!isRecord(property) || property.name !== 'refrigerant') continue;
    const first = Array.isArray(property.values) ? property.values.find(isRecord) : undefined;
    // Manifolds : le fluide est dans `description` ; sondes connectées : dans la valeur.
    const fromValue = first ? text(first.value) || text(first.description) : '';
    const fromDescription = text(property.description);
    const refrigerant = fromValue || (fromDescription === 'refrigerant' ? '' : fromDescription);
    return refrigerant || null;
  }
  return null;
}

function parseMeasurement(raw: unknown): TestoMeasurement | null {
  if (!isRecord(raw) || !Array.isArray(raw.channels)) return null;
  const readings: TestoReading[] = [];
  const taken = new Set<TestoQuantity>();
  let emptyChannels = 0;
  for (const channel of raw.channels.slice(0, MAX_CHANNELS)) {
    const reading = parseChannel(channel);
    if (reading === 'empty') emptyChannels += 1;
    else if (reading) {
      // Deux canaux pour une même grandeur : le second reste visible, non affecté.
      if (reading.quantity && taken.has(reading.quantity)) reading.quantity = null;
      if (reading.quantity) taken.add(reading.quantity);
      readings.push(reading);
    }
  }
  return {
    schemaVersion: text(raw.schemaVersion, 12) || null,
    measurementType: (isRecord(raw.type) ? text(raw.type.name) : '') || null,
    measuredAt: parseTimestamp(raw.timeStamp),
    devices: parseDevices(raw.device),
    refrigerant: parseRefrigerant(raw.properties),
    readings,
    emptyChannels,
  };
}

/**
 * Lit un export Testo : une mesure seule, ou une collection de mesures.
 * Lève `TestoParseError` avec un message affichable tel quel.
 */
export function parseTestoExport(content: string): TestoMeasurement[] {
  if (content.length > TESTO_MAX_CHARS) throw new TestoParseError('too_large');
  let json: unknown;
  try {
    // Les exports commencent par une marque d'ordre d'octets que JSON.parse refuse.
    json = JSON.parse(content.replace(/^\uFEFF/, ''));
  } catch {
    throw new TestoParseError('not_json');
  }
  const candidates = isRecord(json) && Array.isArray(json.measurements) ? json.measurements : [json];
  const measurements = candidates
    .slice(0, MAX_MEASUREMENTS)
    .map(parseMeasurement)
    .filter((m): m is TestoMeasurement => m !== null);
  if (measurements.length === 0) throw new TestoParseError('not_testo');
  const withValues = measurements.filter((m) => m.readings.length > 0);
  if (withValues.length === 0) throw new TestoParseError('no_measurement');
  return withValues;
}

/**
 * Champs « relevé » d'une fiche CERFA, dans l'ordre d'affichage. Les clés sont
 * celles du contenu de la fiche : texte libre, corrigeable avant signature.
 */
export const CERFA_MEASURE_FIELDS: { key: string; label: string; quantity?: TestoQuantity }[] = [
  { key: 'measureSource', label: 'Appareil de mesure' },
  { key: 'measureDate', label: 'Date du relevé' },
  { key: 'measureRefrigerant', label: 'Fluide réglé sur l’appareil' },
  { key: 'lowPressure', label: 'Basse pression', quantity: 'lowPressure' },
  { key: 'highPressure', label: 'Haute pression', quantity: 'highPressure' },
  { key: 'evaporationTemp', label: 'Température d’évaporation', quantity: 'evaporationTemp' },
  { key: 'condensationTemp', label: 'Température de condensation', quantity: 'condensationTemp' },
  { key: 'suctionLineTemp', label: 'Température d’aspiration', quantity: 'suctionLineTemp' },
  { key: 'liquidLineTemp', label: 'Température ligne liquide', quantity: 'liquidLineTemp' },
  { key: 'superheat', label: 'Surchauffe', quantity: 'superheat' },
  { key: 'subcooling', label: 'Sous-refroidissement', quantity: 'subcooling' },
];

/** Libellés lisibles du contenu d'une fiche CERFA (écran et récapitulatif PDF). */
export const CERFA_FIELD_LABELS: Record<string, string> = {
  operatorName: 'Opérateur',
  capacityCertificate: 'Attestation de capacité',
  issuerName: 'Émise au nom de',
  issuerAddress: 'Adresse de l’entreprise émettrice',
  issuerSiret: 'SIRET de l’entreprise émettrice',
  operationDate: 'Date de l’opération',
  operationNature: 'Nature de l’opération',
  notes: 'Observations',
  ...CERFA_CONTENT_LABELS,
  ...Object.fromEntries(CERFA_MEASURE_FIELDS.map((field) => [field.key, field.label])),
};

/**
 * Jour du relevé. Une date sans fuseau est déjà l'heure de l'appareil : on la
 * lit telle quelle. Une date avec fuseau (horodatage numérique converti en UTC)
 * est ramenée au jour LOCAL, sinon un relevé de fin de soirée changerait de jour.
 */
function localDay(iso: string): string {
  if (!/(Z|[+-]\d{2}:?\d{2})$/.test(iso)) return iso.slice(0, 10);
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso.slice(0, 10);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Valeurs à reporter sur la fiche pour un relevé ; les grandeurs absentes sont omises. */
export function testoCerfaFields(measurement: TestoMeasurement): Record<string, string> {
  const fields: Record<string, string> = {};
  const source = measurement.devices
    .map((device) => (device.serial ? `${device.name} n° ${device.serial}`.trim() : device.name))
    .join(', ');
  if (source) fields.measureSource = source.slice(0, 200);
  if (measurement.measuredAt) fields.measureDate = localDay(measurement.measuredAt);
  if (measurement.refrigerant) fields.measureRefrigerant = measurement.refrigerant;
  for (const reading of measurement.readings) {
    if (reading.quantity) fields[reading.quantity] = reading.display;
  }
  return fields;
}
